import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { isJeConversionInProgress } from "@common/helpers/jeConversionInProgress";
import { useAccountingMigration } from "@api/accountingIntegration.api";
import { accountingReportsQueryKeys } from "@api/accountingReports.api";
import { useScope } from "@state/scopeContext.lib";

function isAccountingReportQueryForEntity({
    queryKey,
    entityId,
    reportKey,
}: {
    queryKey: readonly unknown[];
    entityId: number | undefined;
    reportKey: string;
}) {
    if (entityId == null || queryKey[0] !== reportKey) {
        return false;
    }

    const filters = queryKey[1];
    if (filters == null || typeof filters !== "object") {
        return false;
    }

    if (!("entityId" in filters) || typeof filters.entityId !== "number") {
        return false;
    }

    return filters.entityId === entityId;
}

export function useJeConversionInProgress(entityId: number | undefined) {
    const { workspaceId } = useScope();
    const queryClient = useQueryClient();
    const { data: accountingMigration } = useAccountingMigration(
        workspaceId ?? null,
        entityId,
        { refetchUntilConversionFinished: !!entityId },
    );

    const isConversionInProgress =
        accountingMigration != null &&
        isJeConversionInProgress(accountingMigration);

    const wasConversionInProgressRef = useRef(false);

    useEffect(() => {
        wasConversionInProgressRef.current = false;
    }, [entityId]);

    useEffect(() => {
        if (wasConversionInProgressRef.current && !isConversionInProgress) {
            for (const reportKey of [
                accountingReportsQueryKeys.profitAndLoss,
                accountingReportsQueryKeys.balanceSheet,
            ] as const) {
                queryClient.invalidateQueries({
                    predicate: (query) =>
                        isAccountingReportQueryForEntity({
                            queryKey: query.queryKey,
                            entityId,
                            reportKey,
                        }),
                });
            }
        }

        wasConversionInProgressRef.current = isConversionInProgress;
    }, [entityId, isConversionInProgress, queryClient]);

    return { isJeConversionInProgress: isConversionInProgress };
}
